import * as ExcelJS from "exceljs";
import FileSaver from "file-saver";
import * as util from ".";
import lodash from "lodash";

type HeaderFormat = "money" | "number";

interface Header {
  common: {
    header: string;
    key: string;
    width: number;
  };
  other: {
    format?: HeaderFormat;
  };
}

export function header(headerName: string, key: string, width: number, format?: HeaderFormat): Header {
  return {
    common: {
      header: headerName,
      key,
      width
    },
    other: {
      format
    }
  };
}

export function writeSpreadsheet(
  filenameOrNull: string,
  sheetName: string,
  headers: Header[],
  content: unknown[]
): Promise<void> {
  let filename = filenameOrNull;
  if (!filename) {
    filename = "Untitled";
  }

  const workbook = new ExcelJS.Workbook();

  const d = new Date();
  workbook.created = d;
  workbook.modified = d;

  const sheet = workbook.addWorksheet(sheetName);

  sheet.columns = headers.map((col) => ({
    ...col.common,
    ...processHeaderOther(col)
  }));
  const rowFont = { bold: false, color: { argb: "FF000000" } };
  const rowFill: ExcelJS.FillPattern = { type: "pattern", pattern: "none", fgColor: { argb: "FFFFFFFF" } };
  for (const item of content) {
    const row = sheet.addRow(item);
    row.font = rowFont;
    row.fill = rowFill;
  }

  const headerStyle: Partial<ExcelJS.Style> = {
    font: { bold: true, color: { argb: "FFFFFFFF" } },
    alignment: { horizontal: "center" },
    fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FF1E88E5" } }
  };
  for (let i = 1; i <= headers.length; i++) {
    const cell = sheet.getRow(1).getCell(i);
    setStyle(cell, headerStyle);
  }

  return workbook.xlsx.writeBuffer().then((buffer) => {
    FileSaver.saveAs(
      new Blob([buffer], {
        type: "application/octet-stream"
      }),
      `${util.cleanFilename(filename)}.xlsx`
    );
  });
}

function setStyle(cell: ExcelJS.Cell, style: Partial<ExcelJS.Style>) {
  cell.style = lodash.cloneDeep(cell.style);
  cell.style = style;
}

function processHeaderOther(h: Header) {
  if (h.other.format === "money") {
    return {
      style: {
        numFmt: "#,##0.00;[Red]-#,##0.00"
      }
    };
  }
  return {};
}

/*type NativeValue = null | string | number | boolean | Date;
function toNativeValue(value: ExcelJS.CellValue): NativeValue {
  if (value == null || value == undefined) return null;
  if (typeof value == "string") return value;
  if (typeof value == "number") return value;
  if (typeof value == "boolean") return value;
  if (value instanceof Date) return value;
  return value.toString();
}*/
type NativeValue = null | string;
interface ReadRow {
  [key: string]: NativeValue;
}
interface ReadHeader {
  key: string;
}
interface ReadSheet {
  headers: ReadHeader[];
  rows: ReadRow[];
}

export function readSpreasheet(file: Blob, hasHeader: boolean): Promise<ReadSheet> {
  const wb = new ExcelJS.Workbook();
  const reader = new FileReader();

  reader.readAsArrayBuffer(file);
  return new Promise((resolve, reject) => {
    reader.onload = () => {
      const buffer = reader.result as ArrayBuffer;
      wb.xlsx
        .load(buffer)
        .then((workbook) => {
          resolve(readWorkbook(workbook, hasHeader));
        })
        .catch((e) => reject(e));
    };
  });
}

function readWorkbook(workbook: ExcelJS.Workbook, hasHeader: boolean): ReadSheet {
  const result: ReadSheet = {
    headers: [],
    rows: []
  };
  workbook.eachSheet((sheet) => {
    sheet.eachRow((row, rowIndex) => {
      if (rowIndex === 1 && hasHeader) {
        row.eachCell((c) => result.headers.push({ key: (c.value || `cell${c.col}`).toString() }));
      } else {
        const rRow: ReadRow = {};
        row.eachCell((c, idx) => {
          const h: ReadHeader = idx > result.headers.length ? { key: `idx_${idx}` } : result.headers[idx - 1];
          rRow[h.key] = !c.value ? null : c.text; //toNativeValue(c.value);
        });
        result.rows.push(rRow);
      }
    });
  });
  return result;
}
